import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || 
    (window.location.hostname === 'localhost' 
      ? 'http://localhost:5000/api' 
      : 'https://era.io.vn/api'),
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  }
});

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: ['Pin', 'User', 'Board', 'Notification', 'Comment'],
  endpoints: (builder) => ({
    // Auth endpoints
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials
      })
    }),
    register: builder.mutation({
      query: (userData) => ({
        url: '/auth/register',
        method: 'POST',
        body: userData
      })
    }),
    getProfile: builder.query({
      query: () => '/auth/profile',
      providesTags: ['User']
    }),
    updateProfile: builder.mutation({
      query: (data) => ({
        url: '/auth/profile',
        method: 'PUT',
        body: data
      }),
      invalidatesTags: ['User']
    }),
    
    // Pins endpoints
    getPins: builder.query({
      query: () => '/pins',
      providesTags: ['Pin']
    }),
    getPin: builder.query({
      query: (id) => `/pins/${id}`,
      providesTags: (result, error, id) => [{ type: 'Pin', id }]
    }),
    createPin: builder.mutation({
      query: (pinData) => ({
        url: '/pins',
        method: 'POST',
        body: pinData
      }),
      invalidatesTags: ['Pin']
    }),
    updatePin: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/pins/${id}`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Pin', id }, 'Pin']
    }),
    deletePin: builder.mutation({
      query: (id) => ({
        url: `/pins/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Pin']
    }),
    likePin: builder.mutation({
      query: (id) => ({
        url: `/pins/${id}/like`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Pin', id }]
    }),
    savePin: builder.mutation({
      query: ({ pinId, boardId }) => ({
        url: '/auth/save-pin',
        method: 'POST',
        body: { pinId, boardId }
      }),
      invalidatesTags: ['Pin', 'Board']
    }),
    
    // Comments endpoints
    getComments: builder.query({
      query: (pinId) => `/pins/${pinId}/comments`,
      providesTags: ['Comment']
    }),
    addComment: builder.mutation({
      query: ({ pinId, content }) => ({
        url: `/pins/${pinId}/comments`,
        method: 'POST',
        body: { content }
      }),
      invalidatesTags: ['Comment']
    }),
    deleteComment: builder.mutation({
      query: ({ pinId, commentId }) => ({
        url: `/pins/${pinId}/comments/${commentId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Comment']
    }),
    
    // Boards endpoints
    getBoards: builder.query({
      query: () => '/boards',
      providesTags: ['Board']
    }),
    createBoard: builder.mutation({
      query: (boardData) => ({
        url: '/boards',
        method: 'POST',
        body: boardData
      }),
      invalidatesTags: ['Board']
    }),
    
    // Notifications endpoints
    getNotifications: builder.query({
      query: () => '/notifications',
      providesTags: ['Notification']
    }),
    markNotificationRead: builder.mutation({
      query: (id) => ({
        url: `/notifications/${id}/read`,
        method: 'PUT'
      }),
      invalidatesTags: ['Notification']
    }),
    
    // Trending endpoints
    getTrending: builder.query({
      query: () => '/trending',
      providesTags: ['Pin']
    }),
    
    // Follow endpoints
    followUser: builder.mutation({
      query: (userId) => ({
        url: `/auth/follow/${userId}`,
        method: 'POST'
      }),
      invalidatesTags: ['User']
    }),
    unfollowUser: builder.mutation({
      query: (userId) => ({
        url: `/auth/unfollow/${userId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['User']
    }),
    
    // Upload endpoints
    uploadImage: builder.mutation({
      query: (formData) => ({
        url: '/upload',
        method: 'POST',
        body: formData
      })
    }),
    deleteImage: builder.mutation({
      query: (publicId) => ({
        url: `/upload/${publicId}`,
        method: 'DELETE'
      })
    })
  })
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useGetPinsQuery,
  useGetPinQuery,
  useCreatePinMutation,
  useUpdatePinMutation,
  useDeletePinMutation,
  useLikePinMutation,
  useSavePinMutation,
  useGetCommentsQuery,
  useAddCommentMutation,
  useDeleteCommentMutation,
  useGetBoardsQuery,
  useCreateBoardMutation,
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useGetTrendingQuery,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useUploadImageMutation,
  useDeleteImageMutation
} = apiSlice;
